/**
 * Pure cart state for the transient (session-only) Agency cart.
 *
 * Product rules (cf. change `catalog-cart`): **1 panier = 1 DMC** (a line from
 * another DMC is refused), duplicates are kept as **distinct lines** (each with
 * its own `lineId` and details). No persistence here — the `CartProvider` owns
 * session storage and `lineId` generation; these helpers are pure and testable.
 */

import type { PriceUnit } from '@/app/api/lib/products/product.pricing';

export type CartLineDetails = {
    startDate?: string | null;
    endDate?: string | null;
    pax?: number | null;
    quantity?: number | null;
    numberNights?: number | null;
    departureTime?: string | null;
    /** Pickup / client hotel ("lieu de rdv"). */
    meetingPointClient?: string | null;
    comment?: string | null;
};

export type CartLine = {
    lineId: string;
    serviceType: string;
    productExternalId: string;
    dmcSlug: string;
    /**
     * `null` quand la page n'a pas pu résoudre l'id, ou pour une ligne restaurée d'une session
     * antérieure à ce champ : le tracking s'abstient plutôt que d'émettre sans `dmc_id`.
     */
    dmcId: string | null;
    dmcName: string;
    productName: string;
    category: string;
    city: string | null;
    /** Product thumbnail (first gallery image), or null when the product has none. */
    imageUrl: string | null;
    unitPrice: number | null;
    currency: string | null;
    /** Display unit of `unitPrice` (drives the cart quantity multiplier). */
    priceUnit: PriceUnit | null;
    variantTitle: string | null;
    /** "Voir la fiche" target (opens in a new tab). */
    productHref: string;
    details: CartLineDetails;
};

export type CartState = {
    lines: CartLine[];
};

export type AddLineResult = { ok: true; state: CartState } | { ok: false; reason: 'different-dmc' };

export function emptyCart(): CartState {
    return { lines: [] };
}

export function isCartEmpty(state: CartState): boolean {
    return state.lines.length === 0;
}

export function cartCount(state: CartState): number {
    return state.lines.length;
}

/** The DMC the cart is bound to (deduced from the first line), or null if empty. */
export function cartDmcSlug(state: CartState): string | null {
    return state.lines[0]?.dmcSlug ?? null;
}

/**
 * Append a line. Refuses a line from a different DMC than the cart's current one
 * (1 panier = 1 DMC). Duplicates of the same product are allowed — the caller
 * supplies a fresh `lineId` per add.
 */
export function addLine(state: CartState, line: CartLine): AddLineResult {
    const currentDmc = cartDmcSlug(state);
    if (currentDmc !== null && currentDmc !== line.dmcSlug) {
        return { ok: false, reason: 'different-dmc' };
    }
    return { ok: true, state: { lines: [...state.lines, line] } };
}

export function removeLine(state: CartState, lineId: string): CartState {
    return { lines: state.lines.filter((line) => line.lineId !== lineId) };
}

export function updateLineDetails(
    state: CartState,
    lineId: string,
    details: CartLineDetails,
): CartState {
    return {
        lines: state.lines.map((line) =>
            line.lineId === lineId ? { ...line, details: { ...line.details, ...details } } : line,
        ),
    };
}
